import { useState, useRef, useEffect } from 'react';
import RichEditor from './RichEditor';
import ImageUploader from '@/components/base/ImageUploader';

const EDGE_FN_URL = 'https://pdxhoigcrxsqwxgrphdv.supabase.co/functions/v1/custom-auth';

const getAdminToken = () => localStorage.getItem('admin_token') ?? '';

async function callProductAPI(action: string, params: Record<string, unknown>) {
  const res = await fetch(EDGE_FN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, token: getAdminToken(), ...params }),
  });
  return res.json();
}

interface Product {
  id: string;
  name: string;
  category: string;
  price: number;
  original_price: number | null;
  images: string[] | null;
  stock: number;
  sales: number;
  status: string;
  description?: string | null;
  sku?: string | null;
  weight?: number | null;
  points_per_box?: number | null;
  boxes_per_order?: number | null;
  sort_order?: number | null;
  shipping_type?: string | null;
  shipping_template?: string | null;
  stock_deduct?: string | null;
  purchase_limit?: number | null;
}

interface AddProductModalProps {
  onClose: () => void;
  onSuccess: () => void;
  product?: Product | null;
}

type TabKey = 'basic' | 'specs' | 'detail' | 'more';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'basic', label: '基本信息' },
  { key: 'specs', label: '规格/库存' },
  { key: 'detail', label: '商品详情' },
  { key: 'more', label: '更多设置' },
];

const CATEGORIES = ['洗发护发', '头皮护理', '护发工具', '套装礼盒', '口服营养', '护发', '护肤', '面膜', '防晒', '洁面', '彩妆', '香水', '保健品', '其他'];

const SHIPPING_TEMPLATES = [
  '全国包邮（除偏远地区）',
  '全国包邮',
  '顺丰速运',
  '普通快递',
  '到付运费',
];

const FormRow = ({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {required && <span className="text-rose-500 mr-0.5">*</span>}
      {label}
    </label>
    {children}
  </div>
);

const AddProductModal = ({ onClose, onSuccess, product }: AddProductModalProps) => {
  const isEdit = !!product;
  const [activeTab, setActiveTab] = useState<TabKey>('basic');

  // ── 基本信息 ──
  const [name, setName] = useState('');
  const [category, setCategory] = useState('护发');
  const [images, setImages] = useState<string[]>([]);
  const [status, setStatus] = useState<'上架' | '下架'>('上架');
  const [sku, setSku] = useState('');
  const [shippingType, setShippingType] = useState('系统默认');
  const [shippingTemplate, setShippingTemplate] = useState(SHIPPING_TEMPLATES[0]);
  const [sortOrder, setSortOrder] = useState('100');

  // ── 规格/库存 ──
  const [price, setPrice] = useState('');
  const [originalPrice, setOriginalPrice] = useState('');
  const [stock, setStock] = useState('');
  const [fakeSales, setFakeSales] = useState('0');
  const [weight, setWeight] = useState('0');
  const [stockDeduct, setStockDeduct] = useState('下单减库存');
  const [purchaseLimitEnabled, setPurchaseLimitEnabled] = useState(false);
  const [purchaseLimit, setPurchaseLimit] = useState('');
  const [pointsPerBox, setPointsPerBox] = useState('');
  const [boxesPerOrder, setBoxesPerOrder] = useState('');

  // ── 商品详情 ──
  const [description, setDescription] = useState('');
  const descriptionRef = useRef<string>('');

  // ── 初始化：product 传入时同步所有字段 ──
  useEffect(() => {
    if (!product) return;
    setName(product.name ?? '');
    setCategory(product.category ?? '护发');
    setImages(product.images ?? []);
    setStatus((product.status as '上架' | '下架') ?? '上架');
    setSku(product.sku ?? '');
    setShippingType(product.shipping_type ?? '系统默认');
    setShippingTemplate(product.shipping_template ?? SHIPPING_TEMPLATES[0]);
    setSortOrder(product.sort_order != null ? String(product.sort_order) : '100');
    setPrice(product.price != null ? String(product.price) : '');
    setOriginalPrice(product.original_price != null ? String(product.original_price) : '');
    setStock(product.stock != null ? String(product.stock) : '');
    setFakeSales(product.sales != null ? String(product.sales) : '0');
    setWeight(product.weight != null ? String(product.weight) : '0');
    setStockDeduct(product.stock_deduct ?? '下单减库存');
    setPurchaseLimitEnabled(!!product.purchase_limit);
    setPurchaseLimit(product.purchase_limit != null ? String(product.purchase_limit) : '');
    setPointsPerBox(product.points_per_box != null ? String(product.points_per_box) : '');
    setBoxesPerOrder(product.boxes_per_order != null ? String(product.boxes_per_order) : '');
    const desc = product.description ?? '';
    setDescription(desc);
    descriptionRef.current = desc;
  }, [product?.id]);

  // ── 错误 & 提交 ──
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // ── 图片管理 ──
  const addImage = (url: string) => {
    if (!url || images.length >= 10) return;
    setImages((prev) => [...prev, url]);
  };
  const removeImage = (idx: number) => setImages((prev) => prev.filter((_, i) => i !== idx));
  const moveImage = (from: number, to: number) => {
    if (to < 0 || to >= images.length) return;
    const next = [...images];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setImages(next);
  };

  // ── 验证 ──
  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = '请输入商品名称';
    if (!price || isNaN(Number(price)) || Number(price) <= 0) errs.price = '请输入有效售价';
    if (!stock || isNaN(Number(stock)) || Number(stock) < 0) errs.stock = '请输入有效库存';
    setErrors(errs);
    if (errs.name) setActiveTab('basic');
    else if (errs.price || errs.stock) setActiveTab('specs');
    return Object.keys(errs).length === 0;
  };

  // ── 提交 ──
  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);

    const payload: Record<string, unknown> = {
      name: name.trim(),
      category,
      price: Number(price),
      original_price: originalPrice && Number(originalPrice) > 0 ? Number(originalPrice) : null,
      stock: Number(stock),
      sales: Number(fakeSales) || 0,
      images: images.length > 0 ? images : null,
      status,
      description: descriptionRef.current.trim() || null,
      sku: sku.trim() || null,
      weight: Number(weight) || 0,
      shipping_type: shippingType,
      shipping_template: shippingTemplate,
      stock_deduct: stockDeduct,
      purchase_limit: purchaseLimitEnabled && purchaseLimit ? Number(purchaseLimit) : null,
      sort_order: Number(sortOrder) || 100,
      points_per_box: pointsPerBox ? Number(pointsPerBox) : null,
      boxes_per_order: boxesPerOrder ? Number(boxesPerOrder) : null,
    };

    const result = isEdit && product
      ? await callProductAPI('product_update', { id: product.id, payload })
      : await callProductAPI('product_insert', { payload });

    if (result.error) {
      setSubmitError(`保存失败：${result.error}`);
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    setSubmitSuccess(true);
    setTimeout(() => {
      onSuccess();
    }, 600);
  };

  const inputCls = (field = '') =>
    `border rounded-lg px-3 py-2 text-sm outline-none transition-colors ${
      field && errors[field]
        ? 'border-rose-300 bg-rose-50 focus:border-rose-400'
        : 'border-gray-200 focus:border-rose-400'
    }`;

  const currentTabIdx = TABS.findIndex((t) => t.key === activeTab);

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="bg-white rounded-2xl w-full flex flex-col"
          style={{ maxWidth: 780, maxHeight: '92vh' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
            <h2 className="font-bold text-gray-900 text-base">{isEdit ? '编辑商品' : '发布新商品'}</h2>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center cursor-pointer text-gray-400 hover:text-gray-600">
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-100 flex-shrink-0 px-6">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-5 py-3 text-sm font-medium cursor-pointer whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-rose-500 text-rose-500'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.key === 'specs' && (errors.price || errors.stock)
                  ? <span className="flex items-center gap-1">{tab.label}<span className="w-1.5 h-1.5 bg-rose-500 rounded-full" /></span>
                  : tab.key === 'basic' && errors.name
                  ? <span className="flex items-center gap-1">{tab.label}<span className="w-1.5 h-1.5 bg-rose-500 rounded-full" /></span>
                  : tab.label}
              </button>
            ))}
          </div>

          {/* Body — 全部 Tab 内容常驻 DOM，用 hidden 控制显示，避免切 Tab 丢失 state */}
          <div className="overflow-y-auto flex-1 px-8 py-6">

            {/* ══ 基本信息 ══ */}
            <div className={activeTab === 'basic' ? '' : 'hidden'}>
              <div className="space-y-6">
                <FormRow label="商品名称" required>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: '' })); }}
                    placeholder="请输入商品名称"
                    className={`w-full ${inputCls('name')}`}
                  />
                  {errors.name && <p className="text-xs text-rose-500 mt-1">{errors.name}</p>}
                </FormRow>

                <FormRow label="商品分类" required>
                  <div className="flex flex-wrap gap-2">
                    {CATEGORIES.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setCategory(c)}
                        className={`px-3 py-1.5 rounded-full text-sm cursor-pointer whitespace-nowrap border transition-all ${
                          category === c
                            ? 'border-rose-500 bg-rose-50 text-rose-600 font-medium'
                            : 'border-gray-200 text-gray-500 hover:border-gray-300'
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </FormRow>

                <FormRow label="商品图片">
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2 items-start">
                      {images.map((url, idx) => (
                        <div key={idx} className="relative group w-20 h-20 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 flex-shrink-0">
                          <img src={url} alt={`图片${idx + 1}`} className="w-full h-full object-cover object-top" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                          {idx === 0 && (
                            <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] text-center py-0.5">首图</span>
                          )}
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                            {idx > 0 && (
                              <button type="button" onClick={() => moveImage(idx, idx - 1)} className="w-6 h-6 flex items-center justify-center bg-white/80 rounded text-gray-700 cursor-pointer">
                                <i className="ri-arrow-left-s-line text-xs" />
                              </button>
                            )}
                            <button type="button" onClick={() => removeImage(idx)} className="w-6 h-6 flex items-center justify-center bg-rose-500 rounded text-white cursor-pointer">
                              <i className="ri-delete-bin-line text-xs" />
                            </button>
                            {idx < images.length - 1 && (
                              <button type="button" onClick={() => moveImage(idx, idx + 1)} className="w-6 h-6 flex items-center justify-center bg-white/80 rounded text-gray-700 cursor-pointer">
                                <i className="ri-arrow-right-s-line text-xs" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                      {images.length < 10 && (
                        <ImageUploader
                          variant="area"
                          size={80}
                          placeholder={`点击上传\n${images.length}/10`}
                          folder="products"
                          onUpload={(url) => addImage(url)}
                        />
                      )}
                    </div>
                    <p className="text-xs text-gray-400">支持 JPG、PNG、WebP，单张最大 10MB，最多 10 张，拖动箭头可调整顺序</p>
                  </div>
                </FormRow>

                <FormRow label="商品状态" required>
                  <div className="flex gap-6">
                    {(['上架', '下架'] as const).map((s) => (
                      <label key={s} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="status" checked={status === s} onChange={() => setStatus(s)} className="accent-rose-500" />
                        <span className="text-sm text-gray-700">{s}</span>
                      </label>
                    ))}
                  </div>
                </FormRow>

                <FormRow label="商品编码">
                  <input type="text" value={sku} onChange={(e) => setSku(e.target.value)} placeholder="请输入商品编码（选填）" className={`w-full ${inputCls()}`} />
                </FormRow>

                <FormRow label="商品排序">
                  <input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} min="0" className={`w-28 ${inputCls()}`} />
                  <p className="text-xs text-gray-400 mt-1">数字越小越靠前</p>
                </FormRow>
              </div>
            </div>

            {/* ══ 规格/库存 ══ */}
            <div className={activeTab === 'specs' ? '' : 'hidden'}>
              <div className="space-y-6">
                <FormRow label="商品价格（积分）" required>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={price}
                      onChange={(e) => { setPrice(e.target.value); setErrors((p) => ({ ...p, price: '' })); }}
                      placeholder="0"
                      min="0"
                      step="1"
                      className={`w-40 ${inputCls('price')}`}
                    />
                    <span className="text-sm text-gray-500">积分</span>
                  </div>
                  {errors.price && <p className="text-xs text-rose-500 mt-1">{errors.price}</p>}
                </FormRow>

                <FormRow label="划线价（积分）">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={originalPrice}
                      onChange={(e) => setOriginalPrice(e.target.value)}
                      placeholder="0（留空则不显示）"
                      min="0"
                      step="1"
                      className={`w-40 ${inputCls()}`}
                    />
                    <span className="text-sm text-gray-500">积分</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">划线价仅用于展示，留空则不显示</p>
                </FormRow>

                <FormRow label="库存数量" required>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={stock}
                      onChange={(e) => { setStock(e.target.value); setErrors((p) => ({ ...p, stock: '' })); }}
                      placeholder="0"
                      min="0"
                      className={`w-40 ${inputCls('stock')}`}
                    />
                    <span className="text-sm text-gray-500">件</span>
                  </div>
                  {errors.stock && <p className="text-xs text-rose-500 mt-1">{errors.stock}</p>}
                </FormRow>

                <FormRow label="虚拟销量">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={fakeSales}
                      onChange={(e) => setFakeSales(e.target.value)}
                      placeholder="0"
                      min="0"
                      className={`w-40 ${inputCls()}`}
                    />
                    <span className="text-sm text-gray-500">件</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">前台展示的销量数字，不影响实际库存，用于提升商品可信度</p>
                </FormRow>

                <FormRow label="商品重量">
                  <div className="flex items-center gap-2">
                    <input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="0" min="0" step="0.01" className={`w-40 ${inputCls()}`} />
                    <span className="text-sm text-gray-500">千克 (Kg)</span>
                  </div>
                </FormRow>

                <FormRow label="库存计算方式">
                  <div className="flex gap-6">
                    {['下单减库存', '付款减库存'].map((s) => (
                      <label key={s} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="stockDeduct" checked={stockDeduct === s} onChange={() => setStockDeduct(s)} className="accent-rose-500" />
                        <span className="text-sm text-gray-700">{s}</span>
                      </label>
                    ))}
                  </div>
                </FormRow>

                <FormRow label="商品限购">
                  <div className="flex gap-6 mb-2">
                    {[{ label: '关闭', val: false }, { label: '开启', val: true }].map((s) => (
                      <label key={s.label} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="purchaseLimit" checked={purchaseLimitEnabled === s.val} onChange={() => setPurchaseLimitEnabled(s.val)} className="accent-rose-500" />
                        <span className="text-sm text-gray-700">{s.label}</span>
                      </label>
                    ))}
                  </div>
                  {purchaseLimitEnabled && (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-sm text-gray-600">每人限购</span>
                      <input type="number" value={purchaseLimit} onChange={(e) => setPurchaseLimit(e.target.value)} placeholder="0" min="1" className={`w-24 ${inputCls()}`} />
                      <span className="text-sm text-gray-500">件</span>
                    </div>
                  )}
                </FormRow>

                <div className="border-t border-gray-100 pt-5">
                  <p className="text-xs text-gray-400 font-medium mb-4">积分设置（选填）</p>
                  <div className="grid grid-cols-2 gap-4">
                    <FormRow label="每盒积分">
                      <div className="flex items-center gap-2">
                        <input type="number" value={pointsPerBox} onChange={(e) => setPointsPerBox(e.target.value)} placeholder="0" min="0" className={`w-32 ${inputCls()}`} />
                        <span className="text-sm text-gray-500">积分/盒</span>
                      </div>
                    </FormRow>
                    <FormRow label="每单盒数">
                      <div className="flex items-center gap-2">
                        <input type="number" value={boxesPerOrder} onChange={(e) => setBoxesPerOrder(e.target.value)} placeholder="1" min="1" className={`w-32 ${inputCls()}`} />
                        <span className="text-sm text-gray-500">盒/单</span>
                      </div>
                    </FormRow>
                  </div>
                </div>
              </div>
            </div>

            {/* ══ 商品详情 ══ */}
            <div className={activeTab === 'detail' ? '' : 'hidden'}>
              <FormRow label="商品详情">
                <RichEditor
                  value={description}
                  onChange={(val) => {
                    setDescription(val);
                    descriptionRef.current = val;
                  }}
                />
              </FormRow>
            </div>

            {/* ══ 更多设置 ══ */}
            <div className={activeTab === 'more' ? '' : 'hidden'}>
              <div className="space-y-6">
                <FormRow label="配送方式">
                  <div className="flex gap-6">
                    {['系统默认', '单独配置'].map((s) => (
                      <label key={s} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="shippingType" checked={shippingType === s} onChange={() => setShippingType(s)} className="accent-rose-500" />
                        <span className="text-sm text-gray-700">{s}</span>
                      </label>
                    ))}
                  </div>
                </FormRow>
                <FormRow label="运费模板">
                  <select value={shippingTemplate} onChange={(e) => setShippingTemplate(e.target.value)} className={`w-80 ${inputCls()} bg-white`}>
                    {SHIPPING_TEMPLATES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </FormRow>
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-emerald-500 text-base mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-emerald-600 leading-relaxed">
                      商品图片已存储至云端，支持长期稳定访问。在「基本信息」标签页可直接点击上传本地图片。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 错误/成功提示 */}
            {submitError && (
              <div className="flex items-center gap-2 bg-rose-50 text-rose-500 text-sm px-4 py-3 rounded-lg mt-4">
                <i className="ri-error-warning-line flex-shrink-0" />
                {submitError}
              </div>
            )}
            {submitSuccess && (
              <div className="flex items-center gap-2 bg-emerald-50 text-emerald-600 text-sm px-4 py-3 rounded-lg mt-4">
                <i className="ri-checkbox-circle-line flex-shrink-0" />
                {isEdit ? '保存成功！' : '发布成功！'}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
            <div className="flex gap-1.5">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`h-2 rounded-full cursor-pointer transition-all ${activeTab === tab.key ? 'bg-rose-500 w-5' : 'bg-gray-200 w-2'}`}
                />
              ))}
            </div>
            <div className="flex gap-3">
              {currentTabIdx > 0 && (
                <button
                  onClick={() => setActiveTab(TABS[currentTabIdx - 1].key)}
                  className="border border-gray-200 text-gray-600 px-5 py-2.5 rounded-lg text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors"
                >
                  上一步
                </button>
              )}
              {currentTabIdx < TABS.length - 1 && (
                <button
                  onClick={() => setActiveTab(TABS[currentTabIdx + 1].key)}
                  className="border border-gray-200 text-gray-600 px-5 py-2.5 rounded-lg text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors"
                >
                  下一步
                </button>
              )}
              <button
                onClick={handleSubmit}
                disabled={submitting || submitSuccess}
                className="bg-rose-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-rose-600 transition-colors disabled:opacity-60 flex items-center gap-2"
              >
                {submitting ? (
                  <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{isEdit ? '保存中...' : '发布中...'}</>
                ) : submitSuccess ? (
                  <><i className="ri-check-line" />{isEdit ? '已保存' : '已发布'}</>
                ) : (
                  isEdit ? '保存修改' : '确认发布'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddProductModal;
